import { ProductCalendar } from './product-calendar/product-calendar';
import { useEffect, useState } from 'react';
import './product-form.scss';
import { QuantityInput } from './quantity-input/quantity-input';
import { DateTime } from 'luxon';
import { Spinner } from '../../components/spinner/spinner';
import { CustomerProduct } from '../../clients/customer-api/product/customer-product';
import { Location } from '../../clients/quipli-service/location/location';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { ProductTrackingType } from '../../clients/quipli-service/product/product';

export const ProductForm = ({
  productId,
  locationId,
  hidePrices,
  setRootAttributes,
  rentalPrice,
  rentalDuration,
}: {
  productId: number;
  locationId: number;
  hidePrices?: string;
  rentalPrice?: string;
  rentalDuration?: string;
  setRootAttributes: (o: any) => void;
}) => {
  const { getRentalAvailability, getProductById, getLocationById } = useCustomerAPI();
  const [quantity, setQuantity] = useState<string>();
  const [quantityNumber, setQuantityNumber] = useState<number>();
  const [location, setLocation] = useState<Location>();
  const [product, setProduct] = useState<CustomerProduct>();
  const [hasAvailability, setHasAvailability] = useState<boolean>(true);

  useEffect(() => {
    setQuantityNumber(parseFloat(quantity || ''));
  }, [quantity]);

  const checkHasAvailability = async () => {
    if (
      product &&
      location &&
      location.checkAvailability &&
      product.trackingType !== ProductTrackingType.NonInventory
    ) {
      const availability = await getRentalAvailability(product.id, location.id, product.minimumOrderQuantity || 1);
      setHasAvailability(
        !availability?.every(
          (av) => av.start && av.start.diff(DateTime.now().plus(location.maximumLeadTime)).milliseconds > 0,
        ),
      );
    }
  };

  useEffect(() => {
    checkHasAvailability();
  }, [product, location]);

  useEffect(() => {
    setRootAttributes({ 'data-has-availability': hasAvailability });
  }, [hasAvailability]);

  useEffect(() => {
    setRootAttributes({
      'data-quantity': `${quantityNumber}`,
    });
  }, [setRootAttributes, quantityNumber]);

  useEffect(() => {
    const refreshProduct = async () => {
      const product = await getProductById(productId);
      setProduct(product);
    };

    setProduct(undefined);
    refreshProduct();
  }, [productId]);

  useEffect(() => {
    const refreshLocation = async () => {
      const location = await getLocationById(locationId);
      setLocation(location);
    };

    setLocation(undefined);
    refreshLocation();
  }, [locationId]);

  useEffect(() => {
    if (quantity === undefined && product) {
      setQuantity(product.recommendedQuantity.toString());
    }
  }, [product, quantity]);

  return product && location ? (
    hasAvailability ? (
      <div className="product-form">
        <ProductCalendar
          product={product}
          quantity={quantityNumber || 1}
          location={location}
          setRootAttributes={setRootAttributes}
        />
        <div className="quantity-price-wrapper">
          <QuantityInput
            name="rental_quantity"
            value={quantity}
            min={product?.minimumOrderQuantity}
            max={product?.maxQuantityAvailable}
            increment={product?.quantityIncrement}
            onChange={setQuantity}
          />

          {hidePrices !== 'true' && !product.hidePrices && location.showRentalPrice && (
            <div className="rental-price-wrapper">
              <div className="rental-duration">
                <span>Price {rentalDuration ? `for ${rentalDuration}` : 'N/A'}</span>
              </div>
              <span className="rental-price">{rentalPrice}</span>
            </div>
          )}
        </div>
      </div>
    ) : (
      <div className="product-form no-inventory">No inventory is currently available for this product</div>
    )
  ) : (
    <Spinner />
  );
};
